import React, { useState, useEffect } from 'react'
import { WorkflowService } from '../../services/workflowService'
import type { WorkflowModelVersion } from '../../services/workflowService'

interface WorkflowsListPageProps {
  onNavigate: (page: string, data?: any) => void
}

export function WorkflowsListPage({ onNavigate }: WorkflowsListPageProps) {
  const [workflows, setWorkflows] = useState<WorkflowModelVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter states
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortBy, setSortBy] = useState('updatedAt_desc')

  useEffect(() => {
    loadWorkflows()
  }, [keyword, statusFilter, sortBy])

  const loadWorkflows = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await WorkflowService.getAll({
        q: keyword || undefined,
        status: statusFilter || undefined
      })

      // フロントエンドでソート
      const sorted = [...data.items].sort((a, b) => {
        const [field, direction] = sortBy.split('_')
        let aValue: any = a[field as keyof WorkflowModelVersion]
        let bValue: any = b[field as keyof WorkflowModelVersion]

        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })

      setWorkflows(sorted)
    } catch (err) {
      console.error('Failed to load workflows:', err)
      setError('ステータスフローの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    onNavigate('workflow-detail')
  }

  const handleEdit = (workflowVersionId: string) => {
    onNavigate('workflow-detail', { workflowVersionId })
  }

  const SORT_OPTIONS = [
    { value: 'workflowName_asc', label: 'ステータスフロー名（A-Z）' },
    { value: 'workflowName_desc', label: 'ステータスフロー名（Z-A）' },
    { value: 'updatedAt_asc', label: '更新日時（古い順）' },
    { value: 'updatedAt_desc', label: '更新日時（新しい順）' }
  ]

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
      DRAFT: { bg: '#f3f4f6', text: '#374151', label: '編集中' },
      PUBLISHED: { bg: '#d1fae5', text: '#065f46', label: '確定' },
      ARCHIVED: { bg: '#fef3c7', text: '#92400e', label: '廃止' }
    }
    const style = statusStyles[status] || statusStyles.DRAFT
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        fontWeight: '500',
        borderRadius: '0.25rem',
        backgroundColor: style.bg,
        color: style.text
      }}>
        {style.label}
      </span>
    )
  }

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString)
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="px-8 py-6">
      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">ステータスフロー管理</h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '1rem',
          alignItems: 'end'
        }}>
          {/* キーワード検索 */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>キーワード検索</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="ステータスフロー名またはIDで検索"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          {/* ステータス */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>ステータス</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <option value="">すべて</option>
              <option value="DRAFT">ドラフト</option>
              <option value="PUBLISHED">公開中</option>
              <option value="ARCHIVED">アーカイブ</option>
            </select>
          </div>
        </div>

        {/* ソート選択と件数表示 */}
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 7L12 3L16 7M8 17L12 21L16 17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              paddingLeft: '0.75rem',
              paddingRight: '2rem',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6'
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            fontWeight: '500'
          }}>
            {workflows.length}件のステータスフロー
          </div>
        </div>
      </div>

      {/* 作成ボタン */}
      <div className="mb-4">
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          + 新規作成
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">読み込み中...</div>
        ) : workflows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">ステータスフローがありません</div>
        ) : (
          <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータスフローID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータス</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータスフロー名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>更新日時</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>更新者</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {workflows.map((workflow) => (
                <tr key={workflow.workflowVersionId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {workflow.workflowVersionId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {getStatusBadge(workflow.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {workflow.workflowName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {formatDateTime(workflow.updatedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {workflow.updatedBy || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => handleEdit(workflow.workflowVersionId)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      開く
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
